import React, { useState } from 'react'
import { Loader2, AlertTriangle } from 'lucide-react'
import { Drawer } from '../ui'
import AccountPicker from '../ui/AccountPicker'
import CategoryPicker from '../ui/CategoryPicker'
import TagPicker from '../attribution-tags/TagPicker'
import { useToast } from '../../contexts/ToastContext'
import useAssetStore from '../../stores/assetStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { assetService } from '../../services/assetService'
import { AssetStatus } from '../../types/asset'

interface AddAssetDrawerProps {
  isOpen: boolean
  onClose: () => void
  onSave?: () => void
}

const AddAssetDrawer: React.FC<AddAssetDrawerProps> = ({ isOpen, onClose, onSave }) => {
  const { showSuccess, showError } = useToast()
  const { createAsset } = useAssetStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [categoryIds, setCategoryIds] = useState<string[]>([])
  const [status, setStatus] = useState<AssetStatus>('Draft')
  const [accountIds, setAccountIds] = useState<string[]>([])
  const [hashtagIds, setHashtagIds] = useState<number[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const [activeValidationWarning, setActiveValidationWarning] = useState<string | null>(null)

  const resetForm = () => {
    setName('')
    setDescription('')
    setCategoryIds([])
    setStatus('Draft')
    setAccountIds([])
    setHashtagIds([])
    setActiveValidationWarning(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedCustomer) {
      showError('Error', 'No customer selected')
      return
    }

    // Validation
    if (!name.trim()) {
      showError('Validation Error', 'Asset name is required')
      return
    }

    if (name.length > 30) {
      showError('Validation Error', 'Asset name must be 30 characters or less')
      return
    }

    // Validate requirements for Active status (case-insensitive check)
    if (status.toLowerCase() === 'active') {
      const missingFields: string[] = []
      if (accountIds.length === 0) missingFields.push('at least one account')
      if (hashtagIds.length === 0) missingFields.push('at least one attribution tag')
      if (categoryIds.length === 0) missingFields.push('at least one post format')

      if (missingFields.length > 0) {
        const message = missingFields.length === 1
          ? missingFields[0]
          : missingFields.length === 2
            ? `${missingFields[0]} and ${missingFields[1]}`
            : `${missingFields.slice(0, -1).join(', ')} and ${missingFields[missingFields.length - 1]}`
        setActiveValidationWarning(
          `To set this asset to Active, you must select ${message}.`
        )
        return
      }
    }

    // Clear any previous warning
    setActiveValidationWarning(null)

    setIsCreating(true)

    try {
      const newAsset = await createAsset(String(selectedCustomer.id), {
        name: name.trim(),
        description: description.trim() || undefined,
        status
      })

      // If categories are selected, set them on the asset
      if (categoryIds.length > 0) {
        await assetService.setAssetCategories(newAsset.id, String(selectedCustomer.id), categoryIds)
      }

      // If accounts are selected, set them on the asset
      if (accountIds.length > 0) {
        await assetService.setAssetAccounts(newAsset.id, String(selectedCustomer.id), accountIds)
      }

      // If hashtags are selected, set them on the asset
      if (hashtagIds.length > 0) {
        await assetService.setAssetHashtags(newAsset.id, String(selectedCustomer.id), hashtagIds)
      }

      showSuccess('Asset Created', `"${newAsset.name}" has been created successfully`)

      // Notify parent to reload with current filters
      onSave?.()

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError(
        'Failed to Create Asset',
        errorMessage.includes('already exists')
          ? 'An asset with this name already exists'
          : 'Please try again later'
      )
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} title='Add Asset'>
      <form onSubmit={handleSubmit} className='flex flex-col h-full'>
        <div className='flex-1 overflow-y-auto px-6 py-4 space-y-3'>
          {/* Active Status Validation Warning */}
          {activeValidationWarning && (
            <div className='flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
              <AlertTriangle className='h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0' />
              <p className='text-xs text-amber-800'>{activeValidationWarning}</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label htmlFor='name' className='block text-xs font-medium text-gray-700 mb-1'>
              Asset Name <span className='text-red-500'>*</span>
            </label>
            <input
              id='name'
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isCreating}
              className='w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed'
              placeholder='e.g., Game Day Recap, Athlete Spotlight'
              maxLength={30}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor='description' className='block text-xs font-medium text-gray-700 mb-1'>
              Description
            </label>
            <textarea
              id='description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isCreating}
              rows={3}
              className='w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none'
              placeholder='Brief description of this asset...'
            />
          </div>

          {/* Post Format */}
          <CategoryPicker
            selectedCategoryIds={categoryIds}
            onChange={setCategoryIds}
            customerId={selectedCustomer?.id}
            disabled={isCreating}
          />

          {/* Platform Accounts */}
          <AccountPicker
            selectedAccountIds={accountIds}
            onChange={setAccountIds}
            customerId={selectedCustomer?.id}
            disabled={isCreating}
          />

          {/* Attribution Tags */}
          <TagPicker
            selectedTagIds={hashtagIds}
            onChange={setHashtagIds}
            customerId={selectedCustomer?.id}
            disabled={isCreating}
          />

          {/* Status */}
          <div>
            <label htmlFor='status' className='block text-xs font-medium text-gray-700 mb-1'>
              Status
            </label>
            <select
              id='status'
              value={status}
              onChange={(e) => setStatus(e.target.value as AssetStatus)}
              disabled={isCreating}
              className='w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed'
            >
              <option value='Draft'>Draft</option>
              <option value='Active'>Active</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className='border-t border-gray-200 px-6 py-4 bg-gray-50'>
          <div className='flex space-x-3'>
            <button
              type='button'
              onClick={handleClose}
              disabled={isCreating}
              className='flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isCreating}
              className='flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isCreating ? (
                <>
                  <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
                  Creating...
                </>
              ) : (
                'Create Asset'
              )}
            </button>
          </div>
        </div>
      </form>
    </Drawer>
  )
}

export default AddAssetDrawer
